import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useDataDictionary, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictValueByCode } from '../utils/i18n';
import { Loading } from '../components';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft, Star, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const JobPostingRecommendationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recommended, setRecommended] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobTitle, setJobTitle] = useState('');
  const { dictionaries: nationalityDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.NATIONALITY });

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const [recRes, postRes] = await Promise.all([
          apiService.jobPostings.getRecommendedHelpers(parseInt(id), 100),
          apiService.jobPostings.getById(parseInt(id)),
        ]);
        if (recRes.success && recRes.data) setRecommended(recRes.data.filter((m: any) => m.matchPercent >= 80));
        if (postRes.success && postRes.data) setJobTitle(postRes.data.title ?? '');
      } catch (e) {
        console.error('Error loading recommendations:', e);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id]);

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto max-w-4xl px-4 py-8">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate(`/job-postings/${id}/detail`)}>
          <ArrowLeft className="h-4 w-4" />
          {t('common.backToJobDetail', '返回職位詳情')}
        </Button>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-100 text-yellow-600">
            <Star className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('common.recommendedHelperTitle', '推薦外傭')}</h1>
            {jobTitle && <p className="text-sm text-muted-foreground">{jobTitle}</p>}
          </div>
        </div>

        {recommended.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-16 text-center">
              <Users className="mb-4 h-12 w-12 text-muted-foreground/40" />
              <p className="text-muted-foreground">{t('common.noRecommendedHelpers', '暫無符合條件的外傭')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('common.recommendedHelperCount', '共 {{count}} 位符合條件的外傭', { count: recommended.length })}</p>
            {recommended.map((m) => (
              <Card
                key={m.helper.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/helper/${m.helper.id}`)}
              >
                <CardContent className="flex items-center gap-4 p-5">
                  <Avatar className="h-11 w-11 shrink-0">
                    <AvatarImage src={m.helper.avatarUrl} alt={m.helper.fullName} />
                    <AvatarFallback>{m.helper.fullName?.[0] ?? '?'}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground">{m.helper.fullName}</p>
                    {m.helper.nationality && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{getDictValueByCode(nationalityDicts, m.helper.nationality) || m.helper.nationality}</p>
                    )}
                  </div>
                  <Badge variant={m.matchPercent >= 90 ? 'default' : 'secondary'} className="shrink-0 text-sm">
                    {t('common.matchPercent', '{{n}}% 符合', { n: m.matchPercent })}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default JobPostingRecommendationsPage;
